<template>
  <template v-if="address && address.id === selectedAddressId">
    <div class="flex flex-col gap-5">
      <div>
        <h2 class="text-xl font-bold capitalize-first">
          {{ $t('global.informations') }}
        </h2>
        <div class="grid grid-cols-1 gap-2">
          <div>
            <BaseShowEditInput
              v-model="address.name"
              :label="$t('attributes.address_name')"
              :edition-mode="true"
              name="name"
              :errors="errors.name"
              :required="true"
            />
          </div>
          <div>
            <BaseShowEditInput
              v-model="address.street"
              :label="$t('attributes.road')"
              :edition-mode="true"
              :errors="errors.street"
              name="road"
              :required="true"
              @update:model-value="resetGeolocation"
            />
          </div>
          <div>
            <SelectCity
              v-model:city-id="address.city_id"
              v-model:city="address.city"
              v-model:zip="address.zip"
              v-model:city-object="localCityObject"
              :edition-mode="true"
              :required="true"
              @update:model-value="resetGeolocation"
            />
          </div>
          <div>
            <SelectCountries
              v-model="address.country"
              :edition-mode="true"
              required
              @update:model-value="resetGeolocation"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-col">
        <h2 class="text-xl font-bold capitalize-first">
          {{ $t('global.location') }}
        </h2>
        <div class="mb-3 text-xs text-slate-600">
          long: {{ address.longitude ?? '---' }} lat:
          {{ address.latitude ?? '---' }}
        </div>
        <div v-if="address.latitude && address.longitude" class="h-60">
          <LocationSelectorComponent
            v-model:latitude="address.latitude"
            v-model:longitude="address.longitude"
          />
        </div>
        <div
          v-else
          class="flex h-60 flex-row items-center justify-center bg-gray-200"
        >
          <BaseButton
            v-if="addressComplete"
            :title="$t('global.search')"
            :display-loader="isSearchingLocation"
            :disabled="isSearchingLocation"
            @click="getLocationFromAddress"
          />
        </div>

        <div
          v-if="address?.latitude && address?.longitude"
          class="pointer-events-none mt-3 flex h-60 w-full"
        >
          <img
            v-if="address?.latitude && address?.longitude"
            class="img-responsive img-thumbnail rounded-md"
            :src="`https://maps.googleapis.com/maps/api/streetview?size=800x300&location=${address?.latitude},${address?.longitude}&fov=120&key=${googleKey}`"
          />
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <LoadingComponent />
  </template>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import SelectCity from '@c/SelectAjaxComponent/SelectCity.vue'
import SelectCountries from '@c/InitialStateFormComponent/Enums/SelectCountries.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import LocationSelectorComponent from '@c/Maps/LocationSelectorComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'

export default {
  name: 'AddressModalForm',
  components: {
    BaseButton,
    LocationSelectorComponent,
    LoadingComponent,
    BaseShowEditInput,
    SelectCountries,
    SelectCity
  },
  props: {
    selectedAddressId: {
      type: [String, Number],
      required: false,
      default: null
    },
    wikiAddress: {
      type: Boolean,
      required: false,
      default: false
    },
    placeCityId: {
      type: [String, Number],
      required: false,
      default: null
    },
    // Values already typed by the user in the calling screen, applied to a
    // brand new address so the same information is not asked twice.
    prefill: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      wiki_city: null,
      isSearchingLocation: false
    }
  },
  computed: {
    ...mapGetters({
      address: 'address/getAddress',
      errors: 'auth/getErrors',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    googleKey() {
      return import.meta.env.VITE_GOOGLE_MAP_API_KEY
    },
    localCityObject() {
      return {
        id: this.address?.city_id,
        full_name: `${this.address?.zip ?? ''} ${this.address?.city ?? ''}`
      }
    },
    addressComplete() {
      return this.address.street?.length && this.address.city?.length
    }
  },
  watch: {},
  beforeMount() {
    if (this.selectedAddressId) {
      if (!this.wikiAddress) {
        this.processFetchAddressByIdAction(this.selectedAddressId)
      } else {
        this.processFetchWikiAddressesByIdAction({
          address_id: this.selectedAddressId
        })
      }
    } else {
      // `city_id` is a route param on the wiki city pages only. Opened from a
      // dossier the route has none, hence the `placeCityId` fallback.
      this.processFetchNewAddressAction({
        params: this.wikiAddress
          ? { city_id: this.$route.params.city_id ?? this.placeCityId }
          : {}
      }).then(() => {
        this.applyPrefill()
      })
    }
  },
  methods: {
    ...mapActions({
      processFetchNewAddressAction: 'address/fetchNewAddress',
      processFetchAddressByIdAction: 'address/fetchAddressById',
      processFetchWikiAddressesByIdAction: 'wiki/fetchWikiAddressesById',
      processGetLatLongFromAddressAction: 'wiki/getLatLongFromAddress'
    }),
    ...mapMutations({
      setLongitudeLatitudeToAddress: 'address/setLongitudeLatitudeToAddress'
    }),
    // Only fills what the new address left empty: anything the API already
    // returned wins over the caller's suggestion.
    applyPrefill() {
      if (!this.prefill || !this.address) return

      Object.entries(this.prefill).forEach(([key, value]) => {
        const filled = value !== null && value !== undefined && value !== ''

        if (filled && !this.address[key]) {
          this.address[key] = value
        }
      })
    },
    resetGeolocation() {
      if (this.address.latitude || this.address.longitude) {
        this.address.latitude = null
        this.address.longitude = null
      }
    },
    async getLocationFromAddress() {
      if (this.isSearchingLocation) return

      this.isSearchingLocation = true

      try {
        const location = await this.processGetLatLongFromAddressAction({
          address: [
            this.address.street,
            [this.address.zip, this.address.city].filter(Boolean).join(' '),
            this.address.country
          ]
            .filter(Boolean)
            .join(', ')
        })

        if (location?.latitude && location?.longitude) {
          this.setLongitudeLatitudeToAddress({
            longitude: location.longitude,
            latitude: location.latitude
          })
        } else {
          this.$h.toastDanger(this.$t('global.no_result'))
        }
      } catch (e) {
        // The HTTP client already toasts on error statuses; just stop here.
        console.error(e)
      } finally {
        this.isSearchingLocation = false
      }
    }
  }
}
</script>
